import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import Tab from "@/components/containers/tabs/Tab";
import TabGroup from "@/components/containers/tabs/TabGroup";
import TabList from "@/components/containers/tabs/TabList";
import TabPanel from "@/components/containers/tabs/TabPanel";
import TabPanels from "@/components/containers/tabs/TabPanels";
import FormSkeleton from "@/components/loadings/skeleton/FormSkeleton";
import type { ChildRouteContext } from "@/root";

import ObjectiveDashboard from "../../../components/objective/dashboard/ObjectiveDashboard";
import EvaluationDashboard from "../../../components/review/dashboard/EvaluationDashboard";
import {
  fetchObjectivePeriodsQueryOptions,
  fetchReviewPeriodsQueryOptions,
} from "../../../services";

const tabs = ["objective", "review"] as const;

export const Route = createFileRoute("/_panel/_performance/performance/")({
  component: RouteComponent,
  pendingComponent: FormSkeleton,
  validateSearch: z.object({
    tab: z.enum(tabs).default("review"),
    period: z.coerce.number().int().optional(),
  }),
  beforeLoad: ({ context }) =>
    ({
      crumb: [{ label: context.t("Overview", { ns: "performance.user" }) }],
    }) satisfies ChildRouteContext,
  loaderDeps: ({ search }) => ({ tab: search.tab }),
  loader: ({ context, deps }) =>
    deps.tab === "review"
      ? context.queryClient.prefetchQuery(fetchReviewPeriodsQueryOptions())
      : context.queryClient.prefetchQuery(fetchObjectivePeriodsQueryOptions()),
});

function RouteComponent() {
  const { t } = useTranslation("performance.user");
  const { period, tab } = Route.useSearch();

  const navigate = Route.useNavigate();

  return (
    <TabGroup
      selectedIndex={tabs.indexOf(tab) || 0}
      onChange={(index) =>
        navigate({
          search: { tab: tabs[index] },
        })
      }
    >
      <TabList>
        <Tab>{t("Objectives")}</Tab>
        <Tab>{t("Performance Evaluation")}</Tab>
      </TabList>
      <TabPanels>
        <TabPanel>
          <ObjectiveDashboard periodId={period} />
        </TabPanel>
        <TabPanel>
          <EvaluationDashboard periodId={period} />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  );
}
